import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

type TaskAction = 'claim' | 'release' | 'complete' | 'review' | 'approve' | 'reject' | 'pay';
type TaskStatus = 'loading' | 'success' | 'error';

const TaskTapHandler = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<TaskStatus>('loading');
  const [message, setMessage] = useState('');
  const [details, setDetails] = useState<any>(null);

  // Get all parameters from URL
  const action = searchParams.get('action') as TaskAction;
  const taskId = searchParams.get('taskId');
  const taskTitle = searchParams.get('task');
  const reward = searchParams.get('reward');
  const workerId = searchParams.get('workerId');
  const workerName = searchParams.get('workerName');
  const messageTs = searchParams.get('ts');
  const channel = searchParams.get('channel') || 'C09H9EC5W7N';

  useEffect(() => {
    const handleTaskAction = async () => {
      if (!action || !taskId) {
        setStatus('error');
        setMessage('Missing required parameters');
        return;
      }

      try {
        let slackMessage = '';
        let workerMessage = '';

        switch (action) {
          case 'claim':
            // Worker claims a task
            slackMessage = `📋 Task claimed by ${workerName || 'worker'}\n*Task:* ${taskTitle}\n*Reward:* ${reward}`;
            workerMessage = `You've claimed the task! Get to work! 💪`;

            // Delete original task posting
            if (messageTs) {
              await fetch('https://futr-bet-api.b-9f2.workers.dev/slack/delete', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ timestamp: messageTs, channel })
              });
            }

            // Post claim confirmation
            await fetch('https://futr-bet-api.b-9f2.workers.dev/slack/post', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ text: slackMessage, channel })
            });

            // Send DM to worker with management buttons
            const manageUrl = `https://futr.bet/tasktap/manage/${taskId}`;
            await fetch('https://futr-bet-api.b-9f2.workers.dev/slack/post', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                text: `✅ Task Claimed!\n\n${taskTitle}\n💰 ${reward}\n\nManage: ${manageUrl}`,
                channel: workerId // DM to worker
              })
            });
            break;

          case 'release':
            // Worker releases a task back to pool
            slackMessage = `🔄 Task released back to pool\n*Task:* ${taskTitle}\n*Available for:* ${reward}`;
            workerMessage = `Task released. It's now available for others.`;

            // Repost the task as available
            const reclaimUrl = `https://futr.bet/tasktap/handler?action=claim&taskId=${taskId}&task=${encodeURIComponent(taskTitle || '')}&reward=${reward}`;
            await fetch('https://futr-bet-api.b-9f2.workers.dev/slack/post', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                text: `📢 Task Available!\n\n*Task:* ${taskTitle}\n*Reward:* ${reward}\n\n👉 Claim: ${reclaimUrl}`,
                channel
              })
            });
            break;

          case 'complete':
            // Worker marks task as complete, needs review
            slackMessage = `✅ Task completed, pending review\n*Task:* ${taskTitle}\n*Worker:* ${workerName}`;
            workerMessage = `Task submitted! Waiting for review... 🔍`;

            // Post to review channel
            const reviewUrl = `https://futr.bet/tasktap/handler?action=review&taskId=${taskId}&workerId=${workerId}`;
            await fetch('https://futr-bet-api.b-9f2.workers.dev/slack/post', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                text: `🔍 Review Needed!\n\n*Task:* ${taskTitle}\n*Completed by:* ${workerName}\n*Reward:* ${reward}\n\n✅ Approve: ${reviewUrl}&decision=approve\n❌ Reject: ${reviewUrl}&decision=reject`,
                channel: 'C09H9EC5W7N' // Review channel
              })
            });
            break;

          case 'review':
            const decision = searchParams.get('decision');
            if (decision === 'approve') {
              // Task approved - trigger payment
              slackMessage = `✅ Task Approved!\n*Task:* ${taskTitle}\n*Payment:* ${reward} to ${workerName}`;

              // Send payment confirmation to worker
              await fetch('https://futr-bet-api.b-9f2.workers.dev/slack/post', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  text: `💰 Payment Sent!\n\nYour task "${taskTitle}" was approved!\n*Amount:* ${reward}\n\n🎉 Great work!`,
                  channel: workerId
                })
              });

              workerMessage = 'Task approved! Payment sent! 💰';
            } else {
              // Task rejected - needs revision
              slackMessage = `❌ Task Needs Revision\n*Task:* ${taskTitle}\n*Worker:* ${workerName}`;

              // Notify worker
              await fetch('https://futr-bet-api.b-9f2.workers.dev/slack/post', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  text: `⚠️ Revision Needed\n\nYour task "${taskTitle}" needs more work.\nPlease review and resubmit.`,
                  channel: workerId
                })
              });

              workerMessage = 'Task needs revision. Please review feedback.';
            }

            // Delete review request message
            if (messageTs) {
              await fetch('https://futr-bet-api.b-9f2.workers.dev/slack/delete', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ timestamp: messageTs, channel: 'C09H9EC5W7N' })
              });
            }

            // Post final decision
            await fetch('https://futr-bet-api.b-9f2.workers.dev/slack/post', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ text: slackMessage, channel: 'C09H9EC5W7N' })
            });
            break;

          default:
            throw new Error(`Unknown action: ${action}`);
        }

        // Store in D1 for tracking
        await fetch('https://futr-bet-api.b-9f2.workers.dev/tasktap/log', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            taskId,
            action,
            workerId,
            workerName,
            timestamp: new Date().toISOString(),
            details: { taskTitle, reward }
          })
        }).catch(console.error);

        setStatus('success');
        setMessage(workerMessage || `${action} successful!`);
        setDetails({ taskTitle, reward, workerId, workerName });

      } catch (error) {
        console.error('Error:', error);
        setStatus('error');
        setMessage('Something went wrong. Please try again.');
      }
    };

    handleTaskAction();
  }, [action, taskId, taskTitle, reward, workerId, workerName, messageTs, channel, searchParams]);

  const getActionEmoji = () => {
    switch (action) {
      case 'claim': return '📋';
      case 'release': return '🔄';
      case 'complete': return '✅';
      case 'review': return '🔍';
      case 'approve': return '✅';
      case 'reject': return '❌';
      case 'pay': return '💰';
      default: return '📝';
    }
  };

  const getActionColor = () => {
    switch (action) {
      case 'claim': return 'blue';
      case 'release': return 'yellow';
      case 'complete': return 'green';
      case 'approve': return 'green';
      case 'pay': return 'green';
      case 'reject': return 'red';
      default: return 'gray';
    }
  };

  const colorClasses = {
    blue: 'bg-blue-900/30 border-blue-500',
    yellow: 'bg-yellow-900/30 border-yellow-500',
    green: 'bg-green-900/30 border-green-500',
    red: 'bg-red-900/30 border-red-500',
    gray: 'bg-gray-800 border-gray-600'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-white flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {status === 'loading' && (
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-400 mx-auto mb-4"></div>
            <p className="text-gray-400">Processing {action}...</p>
          </div>
        )}

        {status === 'success' && (
          <div className={`${colorClasses[getActionColor()]} border rounded-lg p-8 text-center`}>
            <div className="text-6xl mb-4">{getActionEmoji()}</div>
            <h1 className="text-3xl font-bold mb-2 capitalize">{action} Successful!</h1>

            {details?.taskTitle && (
              <p className="text-xl mb-2">{details.taskTitle}</p>
            )}

            <p className="text-gray-300 mb-4">{message}</p>

            {details?.reward && (
              <div className="mt-4 pt-4 border-t border-gray-700">
                <p className="text-sm text-gray-400">Reward: {details.reward}</p>
              </div>
            )}

            <div className="mt-6">
              <p className="text-xs text-gray-500">This window can be closed</p>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="bg-gray-800 border border-gray-600 rounded-lg p-8 text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <h1 className="text-3xl font-bold mb-2">Error</h1>
            <p className="text-gray-300">{message}</p>
            <p className="text-sm text-gray-500 mt-4">Please check Slack or try again.</p>
          </div>
        )}

        {/* Debug info */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-8 p-4 bg-gray-800 rounded text-xs font-mono">
            <p>Action: {action}</p>
            <p>Task ID: {taskId}</p>
            <p>Task: {taskTitle}</p>
            <p>Reward: {reward}</p>
            <p>Worker: {workerName} ({workerId})</p>
            <p>Message TS: {messageTs}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskTapHandler;